import React, {FC} from 'react';
import {cx} from './cx';

export type IconName =
  | 'arrow-down'
  | 'arrow-left'
  | 'arrow-right'
  | 'bell'
  | 'book-open'
  | 'camera'
  | 'check'
  | 'chevron-down'
  | 'close'
  | 'cup'
  | 'external'
  | 'gift'
  | 'grid'
  | 'heart'
  | 'home'
  | 'link'
  | 'lock'
  | 'menu'
  | 'minus'
  | 'mountain'
  | 'pen'
  | 'plus'
  | 'refresh'
  | 'search'
  | 'share'
  | 'sparkle'
  | 'users';

interface IconProps {
  name: IconName;
  size?: number;
  className?: string;
  label?: string;
  filled?: boolean;
}

const paths: Record<IconName, React.ReactNode> = {
  'arrow-down': (
    <>
      <path d="M12 5v14" />
      <path d="m6 13 6 6 6-6" />
    </>
  ),
  'arrow-left': (
    <>
      <path d="M19 12H5" />
      <path d="m11 6-6 6 6 6" />
    </>
  ),
  'arrow-right': (
    <>
      <path d="M5 12h14" />
      <path d="m13 6 6 6-6 6" />
    </>
  ),
  bell: (
    <>
      <path d="M18 8a6 6 0 0 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9Z" />
      <path d="M10 21h4" />
    </>
  ),
  'book-open': (
    <>
      <path d="M12 6C9 4 6 3.5 3 4v15c3-.5 6 0 9 2 3-2 6-2.5 9-2V4c-3-.5-6 0-9 2Z" />
      <path d="M12 6v15" />
    </>
  ),
  camera: (
    <>
      <path d="M4 7h3l2-3h6l2 3h3a1 1 0 0 1 1 1v11a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V8a1 1 0 0 1 1-1Z" />
      <circle cx="12" cy="13" r="3.5" />
    </>
  ),
  check: <path d="m5 12 4 4L19 6" />,
  'chevron-down': <path d="m6 9 6 6 6-6" />,
  close: (
    <>
      <path d="m6 6 12 12" />
      <path d="M18 6 6 18" />
    </>
  ),
  cup: (
    <>
      <path d="M4 8h12v7a5 5 0 0 1-5 5H9a5 5 0 0 1-5-5Z" />
      <path d="M16 9h2a3 3 0 0 1 0 6h-2M7 2v2m6-2v2" />
    </>
  ),
  external: (
    <>
      <path d="M14 5h5v5" />
      <path d="m10 14 9-9" />
      <path d="M19 13v6H5V5h6" />
    </>
  ),
  gift: (
    <>
      <path d="M4 10h16v10H4z" />
      <path d="M2.5 6.5h19v4h-19z" />
      <path d="M12 6.5V20" />
      <path d="M12 6.5C9 6.5 7 5.4 7 3.8c0-1 .8-1.8 1.9-1.8C10.7 2 12 4.1 12 6.5Z" />
      <path d="M12 6.5c3 0 5-1.1 5-2.7 0-1-.8-1.8-1.9-1.8C13.3 2 12 4.1 12 6.5Z" />
    </>
  ),
  grid: (
    <>
      <rect x="3" y="3" width="7" height="7" rx="1.5" />
      <rect x="14" y="3" width="7" height="7" rx="1.5" />
      <rect x="3" y="14" width="7" height="7" rx="1.5" />
      <rect x="14" y="14" width="7" height="7" rx="1.5" />
    </>
  ),
  heart: (
    <path d="M20.8 5.7a5.4 5.4 0 0 0-7.7 0L12 6.8l-1.1-1.1a5.4 5.4 0 0 0-7.7 7.7L12 22l8.8-8.6a5.4 5.4 0 0 0 0-7.7Z" />
  ),
  home: (
    <>
      <path d="m3 10 9-7 9 7M5 9v11h14V9" />
      <path d="M9 20v-7h6v7" />
    </>
  ),
  link: (
    <>
      <path d="M10 13a5 5 0 0 0 7.1.1l2-2a5 5 0 0 0-7.1-7.1l-1.1 1.1" />
      <path d="M14 11a5 5 0 0 0-7.1-.1l-2 2A5 5 0 0 0 12 20l1.1-1.1" />
    </>
  ),
  lock: (
    <>
      <rect x="5" y="10" width="14" height="11" rx="2" />
      <path d="M8 10V7a4 4 0 0 1 8 0v3M12 15v2" />
    </>
  ),
  menu: (
    <>
      <path d="M4 7h16" />
      <path d="M4 12h16" />
      <path d="M4 17h16" />
    </>
  ),
  minus: <path d="M5 12h14" />,
  mountain: (
    <>
      <path d="m2 20 8-15 5 9 3-5 4 11Z" />
      <path d="m7 11 3 2 3-2" />
    </>
  ),
  pen: (
    <>
      <path d="m15 4 5 5-11 11-6 1 1-6L15 4Z" />
      <path d="m12 7 5 5" />
    </>
  ),
  plus: (
    <>
      <path d="M12 5v14" />
      <path d="M5 12h14" />
    </>
  ),
  refresh: (
    <>
      <path d="M20 10a8 8 0 0 0-13.6-5.7L3 7M3 3v4h4" />
      <path d="M4 14a8 8 0 0 0 13.6 5.7L21 17m0 4v-4h-4" />
    </>
  ),
  search: (
    <>
      <circle cx="10.5" cy="10.5" r="6.5" />
      <path d="m16 16 5 5" />
    </>
  ),
  share: (
    <>
      <circle cx="18" cy="5" r="2.5" />
      <circle cx="6" cy="12" r="2.5" />
      <circle cx="18" cy="19" r="2.5" />
      <path d="m8.2 10.8 7.6-4.5" />
      <path d="m8.2 13.2 7.6 4.5" />
    </>
  ),
  sparkle: (
    <>
      <path d="M12 2c.7 5.5 2.5 7.3 8 8-5.5.7-7.3 2.5-8 8-.7-5.5-2.5-7.3-8-8 5.5-.7 7.3-2.5 8-8Z" />
      <path d="M19 16c.3 2.1.9 2.7 3 3-2.1.3-2.7.9-3 3-.3-2.1-.9-2.7-3-3 2.1-.3 2.7-.9 3-3Z" />
    </>
  ),
  users: (
    <>
      <circle cx="9" cy="8" r="3.2" />
      <path d="M3.5 20v-2.2A5.5 5.5 0 0 1 9 12.3a5.5 5.5 0 0 1 5.5 5.5V20" />
      <path d="M15 5.2a3.2 3.2 0 0 1 0 5.8" />
      <path d="M17 13.2a5.5 5.5 0 0 1 3.5 5.1V20" />
    </>
  ),
};

export const Icon: FC<IconProps> = ({
  name,
  size = 20,
  className,
  label,
  filled = false,
}) => (
  <svg
    aria-hidden={label ? undefined : true}
    aria-label={label}
    className={cx('sui-icon', className)}
    fill={filled ? 'currentColor' : 'none'}
    height={size}
    role={label ? 'img' : undefined}
    stroke="currentColor"
    strokeLinecap="round"
    strokeLinejoin="round"
    strokeWidth="1.8"
    viewBox="0 0 24 24"
    width={size}
  >
    {paths[name]}
  </svg>
);
